import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { reploBuilderFeatureList } from "@v2/features/onboarding/constants/reploBuilderFeatureList";
import { getProductLabels } from "@v2/utils/project-labels";
import { ArrowUpRight, Check, PencilRuler } from "lucide-react";

type ReploHostingType = "replo" | "shopify";

interface OnboardingChooseBuilderTypeProps {
  selectedHostingType: ReploHostingType | null;
  onSelectHostingType: (option: ReploHostingType) => void;
}

interface ProductData {
  id: ReploHostingType;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
  backgroundImage: string;
  decorativeImage: string;
  decorativeImageClasses: string;
  hoverTransform: string;
}

interface ReploHostingTypeCard {
  product: ProductData;
  isSelected: boolean;
  onSelect: (id: ReploHostingType) => void;
}

const ReploHostingTypeCard: React.FC<ReploHostingTypeCard> = ({
  product,
  isSelected,
  onSelect,
}) => {
  // NOTE: We need this state because we don't want to display the black bg when the image is loaded in
  // This is because the image has a fade in/out animation
  const [isBackgroundImageLoaded, setIsBackgroundImageLoaded] =
    React.useState(false);

  return (
    <div
      className={twMerge(
        "flex flex-col flex-1 h-[464px] border rounded-xl p-6 gap-6 cursor-pointer overflow-hidden relative transition-colors duration-300 group",
        isSelected && !isBackgroundImageLoaded ? "bg-black" : "border-border",
      )}
      onClick={() => onSelect(product.id)}
    >
      <img
        src={product.backgroundImage}
        alt=""
        onLoad={() => setIsBackgroundImageLoaded(true)}
        className={twMerge(
          "absolute inset-0 w-full h-full object-cover transition-opacity duration-300 scale-110",
          isSelected ? "opacity-100" : "opacity-0",
        )}
      />
      <div className="flex gap-4 relative z-10">
        {product.id === "replo" && isSelected ? (
          <div className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden">
            <img
              src="/images/onboarding/replo-logo-green-background.svg"
              alt="Replo Builder"
              className="w-full h-full scale-200 object-contain"
            />
          </div>
        ) : (
          product.icon
        )}
        <div className="flex flex-col gap-1">
          <div
            className={twMerge(
              "font-semibold font-editorial text-base transition-colors duration-300 flex items-center gap-2",
              isSelected && "text-brand-seashell",
            )}
          >
            {product.title}
            {product.id === "replo" && (
              <Tag size="sm" variant="beta">
                Beta
              </Tag>
            )}
          </div>
          <div
            className={twMerge(
              "text-sm text-muted transition-colors duration-300",
              isSelected && "text-brand-seashell opacity-80",
            )}
          >
            {product.description}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 relative z-10">
        {product.features.map((feature, index) => (
          <div key={index} className="flex gap-3">
            <div
              className={twMerge(
                "h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-300",
                isSelected ? "bg-brand-seashell" : "bg-black",
              )}
            >
              <Check
                className={twMerge(
                  "w-3 h-3 transition-colors duration-300",
                  isSelected ? "text-black" : "text-white",
                )}
              />
            </div>
            <p
              className={twMerge(
                "text-sm text-muted transition-colors duration-300",
                isSelected && "text-brand-seashell",
              )}
            >
              {feature}
            </p>
          </div>
        ))}
      </div>

      <div
        className={twMerge(
          "absolute transition-all duration-500 bg-no-repeat bg-contain bg-center transform hidden lg:block",
          product.decorativeImageClasses,
          product.hoverTransform,
        )}
        style={{
          backgroundImage: `url(${product.decorativeImage})`,
        }}
        aria-label={product.title}
      />
    </div>
  );
};

const OnboardingChooseBuilderType: React.FC<
  OnboardingChooseBuilderTypeProps
> = ({ selectedHostingType, onSelectHostingType }) => {
  const productLabels = getProductLabels();

  const products: ProductData[] = [
    {
      id: "replo",
      title: productLabels.reploBuilder,
      description:
        "Create a high converting website or landing page for your business in minutes with AI.",
      icon: (
        <img
          src="/images/chat/replo-chat.svg"
          alt={productLabels.reploBuilder}
          className="w-10 h-10 flex-shrink-0 rounded-lg"
        />
      ),
      features: reploBuilderFeatureList,
      backgroundImage: "/images/onboarding/replo-v2-bg-tall.svg",
      decorativeImage: "/images/onboarding/replo-chat.svg",
      decorativeImageClasses: "-bottom-66 w-150 h-150 -right-50",
      hoverTransform: "group-hover:-translate-x-6",
    },
    {
      id: "shopify",
      title: productLabels.shopifyThemeBuilder,
      description:
        "Create pages and publish to an existing Shopify storefront.",
      icon: (
        <div className="w-10 h-10 flex-shrink-0 rounded-lg bg-sheet border border-border flex items-center justify-center">
          <PencilRuler className="w-5 h-5 text-muted" />
        </div>
      ),
      features: [
        "Publish pages into an existing Shopify theme",
        "Use your existing Shopify apps and integrations",
        "Start from conversion-tested templates",
        "Drag-and-drop editor with granular design control",
      ],
      backgroundImage: "/images/onboarding/replo-v2-bg-tall.svg",
      decorativeImage: "/images/onboarding/theme-builder.svg",
      decorativeImageClasses: "-bottom-50 w-120 h-120 right-0",
      hoverTransform: "group-hover:-translate-y-6",
    },
  ];

  return (
    <div className="flex flex-col gap-12">
      <div className="grid gap-4 items-start">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <label className="text-default text-xs md:text-sm font-medium">
            <span>Which product fits your business?</span>
          </label>
          <div className="self-start sm:self-auto">
            <Button
              variant="tertiary"
              onClick={() => {
                return window.open(
                  "https://docs.replo.app/getting-started/builder-types",
                  "_blank",
                );
              }}
              endEnhancer={
                <ArrowUpRight
                  size={14}
                  className="-translate-x-1 -translate-y-0.5"
                />
              }
            >
              What's the difference?
            </Button>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-4 md:gap-8">
          {products.map((product) => (
            <ReploHostingTypeCard
              key={product.id}
              product={product}
              isSelected={selectedHostingType === product.id}
              onSelect={onSelectHostingType}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingChooseBuilderType;
export type { ReploHostingType };
